import React from 'react';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { MapEditorRow } from './MapEditorRow';

interface MapEditorProps {
  value: Record<string, string>;
  onChange: (newValue: Record<string, string>) => void;
}

export const MapEditor: React.FC<MapEditorProps> = ({ value, onChange }) => {
  const entries = Object.entries(value);

  const handleAddEntry = () => {
    // Generate a unique temporary key
    let newKey = 'newKey';
    let counter = 1;
    while (value[newKey] !== undefined) {
      newKey = `newKey${counter}`;
      counter++;
    }
    onChange({ ...value, [newKey]: '' });
  };

  const handleKeyChange = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;

    // If the new key is empty, keep the old key (allows user to clear and retype)
    if (!newKey.trim()) {
      return;
    }

    const newValue = { ...value };
    const oldValue = newValue[oldKey];
    delete newValue[oldKey];
    newValue[newKey] = oldValue;

    onChange(newValue);
  };

  const handleValueChange = (key: string, newValue: string) => {
    onChange({ ...value, [key]: newValue });
  };

  const handleDelete = (key: string) => {
    const newValue = { ...value };
    delete newValue[key];
    onChange(newValue);
  };

  return (
    <Box>
      {entries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
          No traits defined. Click &quot;Add Trait&quot; to get started.
        </Box>
      ) : (
        entries.map(([key, val], index) => (
          <MapEditorRow
            key={index}
            entryKey={key}
            value={val}
            onKeyChange={(newKey) => handleKeyChange(key, newKey)}
            onValueChange={(newValue) => handleValueChange(key, newValue)}
            onDelete={() => handleDelete(key)}
            keyError={!key.trim()}
          />
        ))
      )}
      <Button
        startIcon={<AddIcon />}
        onClick={handleAddEntry}
        size="small"
        sx={{ mt: 1 }}
      >
        Add Trait
      </Button>
    </Box>
  );
};
